import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { api } from '@/api/client'
import { PageHeader } from '@/app/shell'
import { DocumenteEdocs } from '@/ui/documente-edocs'
import { Badge, Card, EmptyState, Skeleton, Suma } from '@/ui/primitives'
import { eDatorie, lei, pereche, perecheDinLei, sursaCurs, type ContextCurs } from '@/lib/money'
import { citesteMod } from '@/ui/curs-selector'

type Factura = {
  id: number; number: string; partner: string | null; vat_no: string | null
  invoice_type: string; total_value: number; currency: string; exchange_rate?: number | null
  total_value_ron: number; payments_ron: number; restant_ron: number
  issue_date: string | null; due_date: string | null; due_dates: string | null
  status: string | null; responsible: string | null; service: string | null
  destination: string | null; booking_number: string | null; booking_customer: string | null
  suppliers: string | null; phone: string | null
}
type Plata = {
  id: number; amount: number; currency: string; exchange_rate: number | null
  payment_date: string | null; payment_method: string | null; reference_number: string | null
  notes: string | null; created_by: string | null
}
type Complet = {
  invoice: Factura; payments: Plata[]; eur_rate: number
  reservation: { id: number; number: string; service?: string | null } | null
}

const zi = (s: string | null) =>
  s ? new Date(s).toLocaleDateString('ro-RO', { day: '2-digit', month: 'long', year: 'numeric' }) : '—'

export function InvoiceDetail() {
  const { id } = useParams()
  const { data, isPending, isError } = useQuery({
    queryKey: ['factura', id],
    queryFn: () => api.get<Complet>(`/invoices/${id}/complete`),
  })

  if (isPending) return (<><PageHeader title="Factura" /><div className="px-8 py-6"><Card><Skeleton /></Card></div></>)
  if (isError || !data?.invoice) return (<><PageHeader title="Factura" /><div className="px-8 py-6"><Card><EmptyState title="Nu am gasit factura" hint={`Nu exista o factura cu id ${id}.`} /></Card></div></>)

  const f = data.invoice
  const doc = { currency: f.currency, exchange_rate: f.exchange_rate ?? null, invoice_type: f.invoice_type }
  const ctx: ContextCurs = { bnrPlus2: data.eur_rate, mod: citesteMod(), manual: null }
  const rate: { date: string; type?: string; percent?: number }[] =
    (() => { try { return f.due_dates ? JSON.parse(f.due_dates) : [] } catch { return [] } })()

  return (
    <>
      <PageHeader
        title={f.number}
        count={f.invoice_type === 'Sales' ? 'factura de vanzare' : 'factura de cumparare'}
        actions={<Link to="/bani/facturi" className="text-[14px] text-muted hover:text-ink">← Toate facturile</Link>}
      />
      <div className="px-8 py-6 grid gap-6 lg:grid-cols-[1fr_340px] items-start">
        <div className="space-y-6 min-w-0">
          <Card className="p-5">
            <div className="flex flex-wrap gap-x-10 gap-y-4">
              <div>
                <p className="text-[12.5px] text-muted">Partener</p>
                <p className="font-medium text-[15px]">{f.partner ?? '—'}</p>
                {f.vat_no && <p className="text-[13px] text-muted tnum">{f.vat_no}</p>}
                {f.phone && <p className="text-[13px] text-muted">{f.phone}</p>}
              </div>
              <div>
                <p className="text-[12.5px] text-muted">Emisa</p>
                <p className="text-[14.5px]">{zi(f.issue_date)}</p>
              </div>
              <div>
                <p className="text-[12.5px] text-muted">Scadenta</p>
                <p className="text-[14.5px]">{zi(f.due_date)}</p>
              </div>
              {f.responsible && (
                <div>
                  <p className="text-[12.5px] text-muted">Responsabil</p>
                  <p className="text-[14.5px]">{f.responsible}</p>
                </div>
              )}
            </div>
            {(f.service || f.destination) && (
              <p className="mt-4 pt-4 border-t border-line text-[14px] text-ink-soft">
                {f.service}{f.destination ? ` · ${f.destination}` : ''}
              </p>
            )}
          </Card>

          {rate.length > 1 && (
            <Card className="p-5">
              <h2 className="text-[13px] font-semibold tracking-[0.08em] uppercase text-muted mb-3">Scadente in rate</h2>
              <div className="flex flex-wrap gap-2">
                {rate.map((r, i) => (
                  <span key={i} className="inline-flex items-center gap-2 h-8 px-3 rounded-full border border-line text-[13px]">
                    <span className="tnum">{zi(r.date)}</span>
                    {r.percent && <span className="text-muted">{r.percent}%</span>}
                    {r.type && <span className="text-muted">{r.type}</span>}
                  </span>
                ))}
              </div>
            </Card>
          )}

          <div>
            <h2 className="text-[13px] font-semibold tracking-[0.08em] uppercase text-muted mb-2">
              Plati ({data.payments?.length ?? 0})
            </h2>
            <Card className="overflow-hidden">
              {(data.payments ?? []).length === 0 ? (
                <EmptyState
                  title="Nicio plata inregistrata aici"
                  hint={f.payments_ron > 1
                    ? `Factura figureaza incasata cu ${lei(f.payments_ron)} lei, dar banii au fost `
                      + 'operati in contabilitate (SAGA), nu inregistrati in aplicatie — de aceea nu apar randuri.'
                    : 'Nu s-a incasat nimic pe aceasta factura.'}
                />
              ) : (
                <table className="w-full text-[14px]">
                  <tbody>
                    {data.payments.map((p) => (
                      <tr key={p.id} className="border-t border-line first:border-0">
                        <td className="px-5 py-3 text-muted tnum text-[13px] w-32">{zi(p.payment_date)}</td>
                        <td className="px-5 py-3 text-[13.5px]">{p.payment_method ?? '—'}</td>
                        <td className="px-5 py-3 text-[13px] text-muted max-w-[200px] truncate">
                          {p.reference_number ?? p.notes ?? ''}
                        </td>
                        <td className="px-5 py-3 text-right tnum">
                          {lei(p.amount)} <span className="text-[11.5px] text-muted">{p.currency}</span>
                          {p.currency !== 'RON' && p.exchange_rate && (
                            <span className="ml-2 text-[12px] text-muted">la {p.exchange_rate.toFixed(4)}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          </div>
        </div>

        <div className="space-y-4">
          <Card className="p-5 space-y-3">
            <div className="flex justify-between items-baseline">
              <span className="text-[13px] text-muted">Total</span>
              <Suma {...pereche(f.total_value, doc, ctx)} explicatie={sursaCurs(doc, ctx)} ton="strong" />
            </div>
            <div className="flex justify-between items-baseline">
              <span className="text-[13px] text-muted">Incasat</span>
              <Suma {...perecheDinLei(f.payments_ron, doc, ctx)} ton="muted" />
            </div>
            <div className="pt-3 border-t border-line flex justify-between items-baseline">
              <span className="text-[13px] text-muted">Rest</span>
              {eDatorie(f.restant_ron)
                ? <Suma {...perecheDinLei(f.restant_ron, doc, ctx)} ton="strong" />
                : <Badge tone="good">Achitata</Badge>}
            </div>
            {f.status && <p className="text-[12.5px] text-muted pt-1">stare in sistem: {f.status}</p>}
          </Card>

          {(f.booking_number || data.reservation) && (
            <Card className="p-5">
              <p className="text-[12.5px] text-muted">Rezervare</p>
              {data.reservation ? (
                <Link to={`/rezervari/${data.reservation.id}`}
                  className="font-medium tnum text-accent hover:underline">{data.reservation.number}</Link>
              ) : <p className="font-medium tnum">{f.booking_number}</p>}
              {f.booking_customer && <p className="text-[13px] text-muted mt-1">titular: {f.booking_customer}</p>}
              {f.suppliers && <p className="text-[13px] text-muted mt-1">furnizori: {f.suppliers}</p>}
            </Card>
          )}

          <DocumenteEdocs tip="invoice" id={f.id} />
        </div>
      </div>
    </>
  )
}
